import React, { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Alert, Button, Group, List, Paper, Progress, Stack, Text, Title } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { TestLanguage } from "types/testTypes";
import { closeVABatchDownloadView } from "@store/ui/uiSlice";
import { LANGUAGE_DATA_MAP } from "@constants/languageConstants";
import { downloadAndPushVAAssetsForLanguage, isAdbConnected } from "@utils/webusb-utils/ADBUtils";
import { VAVideoFailedEntry } from "types/vaVideoTypes";
import {
  getAllVAAssetNames,
  isVAAssetNameLicensed,
  shouldReportVAAssetFailure,
} from "@utils/vaVideoUtils";
import { useAuthContext } from "@service";
import VADownloadLanguageList from "./VADownloadLanguageList";
import VADownloadProgressModal from "./VADownloadProgressModal";

interface VABatchDownloadViewProps {
  onLog?: (msg: string) => void;
}

const VABatchDownloadView: React.FC<VABatchDownloadViewProps> = ({ onLog }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { scopes, scopesLoaded } = useAuthContext();

  const [selectedLanguages, setSelectedLanguages] = useState<TestLanguage[]>([]);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [completedFiles, setCompletedFiles] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const [currentLanguage, setCurrentLanguage] = useState<TestLanguage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [failedFiles, setFailedFiles] = useState<VAVideoFailedEntry[]>([]);

  const abortControllerRef = useRef<AbortController | null>(null);

  const progressValue = totalFiles > 0 ? (completedFiles / totalFiles) * 100 : 0;

  const handleConfirm = async () => {
    setIsConfirmModalOpen(false);

    if (!isAdbConnected()) {
      setError(t("vaDownloadErrorNotConnected"));
      return;
    }

    const assetNames = getAllVAAssetNames();
    const filesPerLanguage = assetNames.length;
    const overall = filesPerLanguage * selectedLanguages.length;

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setTotalFiles(overall);
    setCompletedFiles(0);
    setCurrentLanguage(null);
    setError(null);
    setFailedFiles([]);
    setIsDownloading(true);

    const allFailed: VAVideoFailedEntry[] = [];
    let doneCount = 0;

    try {
      for (const lang of selectedLanguages) {
        if (abortController.signal.aborted) break;

        setCurrentLanguage(lang);
        const languageCode = LANGUAGE_DATA_MAP[lang].languageCode;
        const langLabel = t(LANGUAGE_DATA_MAP[lang].translationKey);

        onLog?.(`⬇️ Starting ${langLabel}`);

        const { failedFiles: downloadFailures } = await downloadAndPushVAAssetsForLanguage(
          languageCode,
          langLabel,
          assetNames,
          (completedForLang) => {
            setCompletedFiles(doneCount + completedForLang);
          },
          abortController.signal,
          onLog,
          (videoName) => isVAAssetNameLicensed(videoName, scopes, scopesLoaded),
        );

        const langFailed = downloadFailures.filter(({ videoName }) =>
          shouldReportVAAssetFailure(videoName, lang, scopes),
        );
        allFailed.push(...langFailed);
        setFailedFiles([...allFailed]);

        langFailed.forEach(({ videoName, reason, error: err }) => {
          const tag = reason === "not_found" ? "not found on server" : "network error";
          onLog?.(`❌ [${langLabel}] ${videoName} — ${tag}: ${err}`);
        });

        onLog?.(
          langFailed.length === 0
            ? `✅ ${langLabel} complete`
            : `⚠️ ${langLabel} complete — ${langFailed.length} failed`,
        );

        doneCount += filesPerLanguage;
      }
    } catch (e: any) {
      if (e?.name !== "AbortError") {
        setError(e?.message ?? String(e));
        onLog?.(`🛑 ${e?.message ?? String(e)}`);
      } else {
        onLog?.("🚫 Download cancelled");
      }
    } finally {
      setCurrentLanguage(null);
      setIsDownloading(false);
      abortControllerRef.current = null;
    }
  };

  const handleCancel = () => {
    abortControllerRef.current?.abort();
  };

  const handleClose = () => {
    dispatch(closeVABatchDownloadView());
  };

  const handleRetry = () => {
    setError(null);
    setFailedFiles([]);
    setCompletedFiles(0);
    setTotalFiles(0);
  };

  const isDone = !isDownloading && totalFiles > 0 && !error && completedFiles >= totalFiles;

  const notFoundFiles = failedFiles.filter((f) => f.reason === "not_found");
  const networkErrorFiles = failedFiles.filter((f) => f.reason === "network_error");

  return (
    <>
      <Paper p="md" withBorder mt="sm">
        <Stack gap="sm">
          <Title order={5}>{t("vaDownloadTitle")}</Title>
          <Text size="sm">{t("vaDownloadDescription")}</Text>

          <VADownloadLanguageList
            selectedLanguages={selectedLanguages}
            onSelectionChange={setSelectedLanguages}
          />

          {(isDownloading || isDone) && (
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                {t("vaDownloadProgressTitle")}
              </Text>

              {selectedLanguages.map((lang) => (
                <Text
                  key={lang}
                  size="sm"
                  fw={currentLanguage === lang ? 700 : 400}
                  c={currentLanguage === lang ? "blue" : undefined}
                >
                  {t(LANGUAGE_DATA_MAP[lang].translationKey)}
                  {currentLanguage === lang ? " …" : ""}
                </Text>
              ))}

              <Progress value={progressValue} animated={isDownloading && progressValue < 100} />

              <Text size="xs" c="dimmed">
                {completedFiles} / {totalFiles}
              </Text>
            </Stack>
          )}

          {isDone && failedFiles.length === 0 && (
            <Alert color="green" title={t("vaDownloadSuccessTitle")} />
          )}

          {notFoundFiles.length > 0 && (
            <Alert
              color="orange"
              title={t("vaDownloadNotFoundTitle", { count: notFoundFiles.length })}
            >
              <List size="xs" spacing={2}>
                {notFoundFiles.map(({ videoName, language }) => (
                  <List.Item key={`${language}-${videoName}`}>
                    [{language}] {videoName}
                  </List.Item>
                ))}
              </List>
            </Alert>
          )}

          {networkErrorFiles.length > 0 && (
            <Alert
              color="red"
              title={t("vaDownloadNetworkErrorTitle", { count: networkErrorFiles.length })}
            >
              <List size="xs" spacing={2}>
                {networkErrorFiles.map(({ videoName, language }) => (
                  <List.Item key={`${language}-${videoName}`}>
                    [{language}] {videoName}
                  </List.Item>
                ))}
              </List>
            </Alert>
          )}

          {error && (
            <Alert color="red" title={t("Error")} onClose={handleRetry} withCloseButton>
              {error}
            </Alert>
          )}

          <Group>
            {!isDownloading && !isDone && (
              <Button
                data-testid="va-install-button"
                onClick={() => setIsConfirmModalOpen(true)}
                disabled={selectedLanguages.length === 0}
              >
                {t("install")}
              </Button>
            )}

            {isDownloading && (
              <Button color="red" variant="outline" onClick={handleCancel}>
                {t("cancel")}
              </Button>
            )}

            {(error || failedFiles.length > 0) && !isDownloading && (
              <Button variant="light" onClick={handleRetry}>
                {t("retry")}
              </Button>
            )}

            {!isDownloading && (
              <Button variant="outline" onClick={handleClose}>
                {t("cancel")}
              </Button>
            )}
          </Group>
        </Stack>
      </Paper>

      <VADownloadProgressModal
        opened={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleConfirm}
        selectedLanguages={selectedLanguages}
      />
    </>
  );
};

export default VABatchDownloadView;
